import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { enUS, he, es, it, fr, ru } from 'date-fns/locale';
import { format, isValid, parse } from 'date-fns';
import { i18nConfig } from 'utils/ui';
import { getCurrency } from 'utils/utils';
import { MoTableData, OrderRow } from 'ts/shared/interfaces/MOResponse';
import { MainMoFilter } from 'constants/mo';
import { CurrencyBanner } from 'ts/shared/interfaces/User';
import { getCurrencyBanner } from 'services/user';
import * as tbLocales from '../../locales/tb';

export interface i18nConfigProps {
  enTranslation: any;
  heTranslation: any;
  ruTranslation: any;
  itTranslation?: any;
  esTranslation?: any;
  frTranslation?: any;
}

let i18nProposal: any;
let i18nClaude: any;
let i18nMainMOPage2: any;
let i18nTB: any;

export const i18nSharedConfig = (props: i18nConfigProps) => {
  const lang = localStorage.getItem('language');

  i18nConfig(props);

  i18n.use(initReactI18next).init({
    resources: {
      en: {
        translation: props.enTranslation,
      },
      he: {
        translation: props.heTranslation,
      },
      ru: {
        translation: props.ruTranslation,
      },
      it: {
        translation: props.itTranslation || {},
      },
      es: {
        translation: props.esTranslation || {},
      },
      fr: {
        translation: props.frTranslation || {},
      },
    },
    lng: lang ? lang : 'en',
    fallbackLng: 'en',

    interpolation: {
      escapeValue: false,
    },
  });

  i18nProposal = i18n.createInstance();
  i18nProposal.use(initReactI18next).init({
    resources: i18n.options.resources,
    lng: lang ? lang : 'en',
    fallbackLng: 'en',

    interpolation: {
      escapeValue: false,
    },
  });

  i18nClaude = i18n.createInstance();
  i18nClaude.use(initReactI18next).init({
    resources: i18n.options.resources,
    lng: lang ? lang : 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

  // Create i18n instance for MainMOPage2 that always uses English
  i18nMainMOPage2 = i18n.createInstance();
  i18nMainMOPage2.use(initReactI18next).init({
    resources: i18n.options.resources,
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

  const paramsTB = {
    resources: {
      en: { translation: { ...tbLocales.enTrTB, ...props.enTranslation } },
      he: { translation: { ...tbLocales.heTrTB, ...props.heTranslation } },
      es: { translation: { ...tbLocales.esTrTB, ...props.esTranslation } },
      it: { translation: { ...tbLocales.itTrTB, ...props.itTranslation } },
      fr: { translation: { ...tbLocales.frTrTB, ...props.frTranslation } },
    },
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  };
  i18nTB = i18n.createInstance();
  i18nTB.use(initReactI18next).init(paramsTB);

  // Override changeLanguage to prevent language changes - always stay in English
  const originalChangeLanguage = i18nMainMOPage2.changeLanguage.bind(i18nMainMOPage2);
  i18nMainMOPage2.changeLanguage = (lng?: string, callback?: (error: any, t: any) => void) => {
    // Always force English, ignore any language change attempts
    return originalChangeLanguage('en', callback);
  };
};

export { i18nProposal, i18nMainMOPage2, i18nClaude, i18nTB };

export const getLocaleSet = (locale?: string) => {
  const lang = locale || localStorage.getItem('language') || 'en';

  switch (lang) {
    case 'he':
      return he;
    case 'es':
      return es;
    case 'it':
      return it;
    case 'fr':
      return fr;
    case 'ru':
      return ru;
    default:
      return enUS;
  }
};

export const dateFormat = (
  date: Date | string | undefined | number,
  formatStr: string,
  locale?: string,
) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const localeSet = getLocaleSet(locale);
  if (dateObj instanceof Date && isNaN(dateObj.getTime())) return '';

  return format(dateObj || new Date(), formatStr, { locale: localeSet });
};

export const formatOccupancySrt = (complect: string) => {
  const adult = complect.match(new RegExp('A', 'g'))?.length;
  const child = complect.match(new RegExp('C', 'g'))?.length;
  const res = [
    { label: `${adult} ${i18n.t('search.adults')}`, value: adult },
    { label: `${child} ${i18n.t('search.children')}`, value: child },
  ];
  return res
    .filter((item) => item.value)
    .map((item) => item.label)
    .join(', ');
};

export const formatOccupancySrtByNumbers = (adults: number, children: number) => {
  const res = [
    { label: `${adults} ${i18n.t('search.adults')}`, value: adults },
    { label: `${children} ${i18n.t('search.children')}`, value: children },
  ];
  return res
    .filter((item) => item.value)
    .map((item) => item.label)
    .join(', ');
};

export const getBounds = (flight: any) => {
  if (flight.outbound && flight.inbound) {
    return [flight.outbound, { ...flight.inbound, flightType: 'return' }];
  } else if (flight.flightBounds?.length) {
    return flight.flightBounds;
  } else {
    return [flight.outbound];
  }
};

export const sanitizeString = (input: string): string => {
  return input.replace(/[^a-zA-Z0-9 \-_.@#?!,:;\u0590-\u05FF\n]/g, '');
};

export const formatPriceProposal = (
  price: number,
  currency: string,
  viewType?: 'p' | 'v' | 'e',
  viewValue?: number,
) => {
  const currencySymbol = getCurrency(currency);
  const currencySymbolBefore = sessionStorage.getItem('currencySymbolBefore') === 'true';

  let val = '';
  if (viewType === 'v' && viewValue) {
    val = `${Math.round(price + viewValue).toLocaleString()}`;
  } else if (viewType === 'p' && viewValue) {
    val = `${Math.round(price * (1 + viewValue / 100)).toLocaleString()}`;
  } else if (viewType === 'e' && viewValue) {
    val = `${Math.round(viewValue).toLocaleString()}`;
  } else {
    val = `${Math.round(price).toLocaleString()}`;
  }
  return currencySymbolBefore ? `${currencySymbol}${val}` : `${val}${currencySymbol}`;
};

export const convertFontSizeToEm = (htmlString: string): string => {
  return htmlString.replace(/font-size:(\d+)px/g, (_, size) => `font-size:${Number(size) / 14}em`);
};
export const sortTableData = (
  data: MoTableData[],
  sortField: string,
  direction: string,
): MoTableData[] => {
  if (!sortField || !direction) return data;

  return [...data].sort((a, b) => {
    const aValue = (a as any)[sortField];
    const bValue = (b as any)[sortField];

    const sortValues = (a: any, b: any): number => {
      if (typeof a === 'string' && typeof b === 'string') {
        const dateA = parse(a, 'dd/MM/yy', new Date());
        const dateB = parse(b, 'dd/MM/yy', new Date());

        if (isValid(dateA) && isValid(dateB)) {
          return dateA.getTime() - dateB.getTime();
        }
      }

      if (a instanceof Date && b instanceof Date) {
        return a.getTime() - b.getTime();
      }

      if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
      }

      if (typeof a === 'boolean' && typeof b === 'boolean') {
        return a === b ? 0 : a ? 1 : -1;
      }

      if (a == null && b == null) return 0;
      if (a == null) return -1;
      if (b == null) return 1;

      return String(a).localeCompare(String(b));
    };

    const result = sortValues(aValue, bValue);
    return direction === 'desc' ? -result : result;
  });
};

export function htmlToBase64(html: string): string {
  const uint8Array = new TextEncoder().encode(html);
  let binary = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
}

export const stripTags = (input: string) => {
  if (typeof input !== 'string') return '';
  return input.replace(/<\/?[^>]+(>|$)/g, '');
};

export const safeDeepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as unknown as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => safeDeepClone(item)) as unknown as T;
  }

  const seen = new WeakMap<object, any>();

  const clone = (value: any): any => {
    if (value === null || typeof value !== 'object') {
      return value;
    }

    if (typeof value === 'function') {
      return undefined;
    }

    if (
      value instanceof Element ||
      value instanceof HTMLElement ||
      (typeof window !== 'undefined' && value === window) ||
      (typeof document !== 'undefined' && value === document) ||
      value?.constructor?.name === 'FiberNode' ||
      value?.$$typeof
    ) {
      return undefined;
    }

    if (value instanceof Date) {
      return new Date(value.getTime());
    }

    if (value instanceof RegExp) {
      return new RegExp(value);
    }

    if (seen.has(value)) {
      return undefined;
    }

    if (Array.isArray(value)) {
      seen.set(value, []);
      const cloned = value.map((item) => clone(item));
      seen.set(value, cloned);
      return cloned;
    }

    seen.set(value, {});
    const cloned: any = {};

    for (const key in value) {
      if (
        key.startsWith('__react') ||
        key.startsWith('__REACT_') ||
        key === '$$typeof' ||
        key === 'stateNode'
      ) {
        continue;
      }

      try {
        const clonedValue = clone(value[key]);
        if (clonedValue !== undefined) {
          cloned[key] = clonedValue;
        }
      } catch (e) {
        continue;
      }
    }

    seen.set(value, cloned);
    return cloned;
  };

  return clone(obj) as T;
};

export const matchesMainFilter = (orderRow: OrderRow, clx: string, mainFilter: string): boolean => {
  if (!mainFilter) return true;

  const clxDate = parse(clx || '', 'dd/MM/yy', new Date());
  switch (mainFilter) {
    case MainMoFilter.CXLLess24Hours:
      if (isValid(clxDate)) {
        const hoursBeforeClx = (new Date().getTime() - clxDate.getTime()) / (1000 * 60 * 60);
        return hoursBeforeClx < 24 && hoursBeforeClx >= 0;
      }
      return false;
    case MainMoFilter.CXLLess48Hours:
      if (isValid(clxDate)) {
        const hoursBeforeClx = (new Date().getTime() - clxDate.getTime()) / (1000 * 60 * 60);
        return hoursBeforeClx < 48 && hoursBeforeClx >= 0;
      }
      return false;
    case MainMoFilter.CXLLess72Hours:
      if (isValid(clxDate)) {
        const hoursBeforeClx = (new Date().getTime() - clxDate.getTime()) / (1000 * 60 * 60);
        return hoursBeforeClx < 72 && hoursBeforeClx >= 0;
      }
      return false;
    case MainMoFilter.CXLLess96Hours:
      if (isValid(clxDate)) {
        const hoursBeforeClx = (new Date().getTime() - clxDate.getTime()) / (1000 * 60 * 60);
        return hoursBeforeClx < 96 && hoursBeforeClx >= 0;
      }
      return false;
    case MainMoFilter.OnlyWithTemporaryNames:
      if (orderRow.mainDisplayPax?.toLowerCase().includes('#t#')) {
        return true;
      }
      return false;
    case MainMoFilter.Open:
      if (orderRow.docket?.toLowerCase().includes('op-')) {
        return true;
      }
      return false;

    case MainMoFilter.Close:
      if (orderRow.docket?.toLowerCase().includes('cp-')) {
        return true;
      }
      return false;

    case MainMoFilter.RefundReport:
      return !!orderRow.haveSaveReport;

    case MainMoFilter.ChangeNameReport:
      return !!orderRow.nameChangeStatus;
    case MainMoFilter.OnlyDuplication:
      return orderRow.isDuplicate;
    case MainMoFilter.CouponCode:
      return !!(orderRow as any).couponCode && (orderRow as any).couponCode.length > 5;
    default:
      return true;
  }
};

export const convertCurrency = async (
  value: number,
  from: string,
  to: string,
): Promise<number | undefined> => {
  const fromCode = from.toUpperCase();
  const toCode = to.toUpperCase();

  if (fromCode === toCode) return value;

  const currencies = await getCurrencyBanner();
  if (!currencies) return undefined;

  const findCurrencyBanner = (
    currencies: Awaited<ReturnType<typeof getCurrencyBanner>>,
    code: string,
  ) => {
    const aliases: Record<string, string[]> = {
      LOC: ['LOC', 'ILS', 'NIS'],
      ILS: ['LOC', 'ILS', 'NIS'],
      NIS: ['LOC', 'ILS', 'NIS'],
    };
    const codes = aliases[code] || [code];
    return currencies?.find((currency) => codes.includes(currency.sysCurrencyCode));
  };

  const currencyRateField: Record<
    string,
    keyof Pick<
      CurrencyBanner,
      'convert_usd_rate' | 'convert_eur_rate' | 'convert_gbp_rate' | 'convert_loc_rate'
    >
  > = {
    USD: 'convert_usd_rate',
    EUR: 'convert_eur_rate',
    GBP: 'convert_gbp_rate',
    LOC: 'convert_loc_rate',
    ILS: 'convert_loc_rate',
    NIS: 'convert_loc_rate',
  };

  const toRateField = currencyRateField[toCode];
  const fromRateField = currencyRateField[fromCode];
  const fromCurrency = findCurrencyBanner(currencies, fromCode);
  const toCurrency = findCurrencyBanner(currencies, toCode);

  if (fromCurrency && toRateField) {
    const rate = fromCurrency[toRateField];
    if (rate) return value * rate;
  }

  if (toCurrency && fromRateField) {
    const rate = toCurrency[fromRateField];
    if (rate) return value / rate;
  }

  return undefined;
};
